/** localStorage + optional Netlify Function for site-wide emergency close */
export const EMERGENCY_STORAGE_KEY = 'lodvik-emergency-closed-date';
export const EMERGENCY_EVENT = 'lodvik-hours-changed';
const STATUS_PATH = '/.netlify/functions/workshop-status';
const TIME_ZONE = 'Europe/Tallinn';

/** Secret form combo — not documented on the site */
export const EMERGENCY_SECRET = {
  name: 'Martin',
  plate: '1111',
  phone: '1234',
  email: 'lodvikou@gmail.com',
  closeMessage: 'kinni',
  openMessage: 'lahti',
} as const;

/** YYYY-MM-DD in Europe/Tallinn */
export function tallinnDateString(date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

let remoteClosedDate: string | null = null;

export type EmergencyAction = 'close' | 'open' | null;

export function matchEmergencyAction(fields: {
  name: string;
  plate: string;
  phone: string;
  email: string;
  message: string;
}): EmergencyAction {
  const name = fields.name.trim();
  const plate = fields.plate.trim();
  const phone = fields.phone.trim();
  const email = fields.email.trim().toLowerCase();
  const message = fields.message.trim().toLowerCase();

  const creds =
    name === EMERGENCY_SECRET.name &&
    plate === EMERGENCY_SECRET.plate &&
    phone === EMERGENCY_SECRET.phone &&
    email === EMERGENCY_SECRET.email;

  if (!creds) return null;
  if (message === EMERGENCY_SECRET.closeMessage) return 'close';
  if (message === EMERGENCY_SECRET.openMessage) return 'open';
  return null;
}

function readLocalClosedDate(): string | null {
  try {
    return localStorage.getItem(EMERGENCY_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLocalClosedDate(date: string | null) {
  try {
    if (date) localStorage.setItem(EMERGENCY_STORAGE_KEY, date);
    else localStorage.removeItem(EMERGENCY_STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

export function isEmergencyClosedToday(now = new Date()): boolean {
  const today = tallinnDateString(now);
  if (readLocalClosedDate() === today) return true;
  if (remoteClosedDate === today) return true;
  return false;
}

function notifyHoursChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EMERGENCY_EVENT));
}

/** Close for Tallinn “today”, or clear emergency close. */
export async function setEmergencyClosed(
  closed: boolean,
): Promise<{ ok: boolean; closedDate: string | null }> {
  const today = tallinnDateString();
  const closedDate = closed ? today : null;

  writeLocalClosedDate(closedDate);
  remoteClosedDate = closedDate;
  notifyHoursChanged();

  // Best-effort site-wide sync (Netlify). Local works even if this fails.
  try {
    const res = await fetch(STATUS_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: closed ? 'close' : 'open',
        // Same secret so the function can verify without a separate API key
        name: EMERGENCY_SECRET.name,
        plate: EMERGENCY_SECRET.plate,
        phone: EMERGENCY_SECRET.phone,
        email: EMERGENCY_SECRET.email,
        message: closed
          ? EMERGENCY_SECRET.closeMessage
          : EMERGENCY_SECRET.openMessage,
      }),
    });
    if (res.ok) {
      const data = (await res.json()) as { closedDate?: string | null };
      if (data.closedDate !== undefined) {
        remoteClosedDate = data.closedDate;
        writeLocalClosedDate(data.closedDate);
      }
    }
  } catch {
    /* offline / local dev without functions */
  }

  return { ok: true, closedDate };
}

/** Pull shared status so all visitors see emergency close. */
export async function refreshEmergencyStatus(): Promise<void> {
  try {
    const res = await fetch(STATUS_PATH, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return;
    const data = (await res.json()) as { closedDate?: string | null };
    const next = data.closedDate ?? null;
    remoteClosedDate = next;
    // Don't overwrite a fresher local close with null from empty remote
    const local = readLocalClosedDate();
    const today = tallinnDateString();
    if (next === today) {
      writeLocalClosedDate(next);
    } else if (local === today && !next) {
      // Keep local until remote confirms; remote null means open
      writeLocalClosedDate(null);
      remoteClosedDate = null;
    } else if (next && next !== today) {
      // Stale remote date — treat as open
      remoteClosedDate = null;
    }
    notifyHoursChanged();
  } catch {
    /* ignore */
  }
}
